import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Chip, Collapse, Paper, Stack, Typography } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from './constants';
import type { ApprovalQueueItem } from './types';

interface Props {
  expanded: boolean;
  queueItem: ApprovalQueueItem;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (itemId: string) => void;
}

export default function ApprovalQueueCard({ expanded, queueItem, onApprovalChange, onToggleDetails }: Props) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1.25,
        overflow: 'hidden',
        ...CHECKLIST_STATUS_STYLES.approvalQueueCard,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
        <WarningAmberIcon color="warning" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700}>
              {queueItem.title}
            </Typography>
            <Chip color="warning" label="Requires approval" size="small" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {queueItem.description}
          </Typography>
        </Box>
        <Button
          color="inherit"
          size="small"
          endIcon={expanded ? <ExpandLessIcon /> : <KeyboardArrowDownIcon />}
          onClick={() => onToggleDetails(queueItem.id)}
        >
          {expanded ? 'Hide details' : 'View details'}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={() => onApprovalChange(queueItem.id)}
        >
          Approve
        </Button>
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Data points
          </Typography>
          <Stack spacing={0} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {queueItem.details.map((detail) => (
              <Stack
                key={detail.label}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ borderBottom: 1, borderColor: 'divider', p: 1.5, '&:last-child': { borderBottom: 0 } }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 220px' }}>
                  {detail.label}
                </Typography>
                <Typography variant="body2">{detail.value}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
